"use client";

/**
 * Create Employer wizard — uses the two-panel modal layout shared with the
 * Export and Connect Account flows: branded left sidebar, numbered step
 * indicator, and animated step transitions.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useMutation } from "@tanstack/react-query";
import { Modal, ModalContent, ModalTitle } from "@/components/ui/modal";
import { Button, SubmitButton } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { showToast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import { logo, websiteToDomain } from "@/lib/logo-dev";
import { useCreateEmployer, useUploadEmployerCover } from "@/lib/api/generated";
import type { EmployerResponse } from "@/lib/api/model";
import { customInstance } from "@/lib/api/client";
import { EmployerResearchScreen } from "../research-screen";

interface CreateEmployerModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (employer: EmployerResponse) => void;
}

interface EnrichResult {
  mission_vision: string | null;
  funding_traction: string | null;
  culture_principles: string | null;
  benefits: string | null;
  cover_url: string | null;
}

type WizardStep = "details" | "enriching";

const ACCEPTED_TYPES = "image/png,image/jpeg";
const MAX_COVER_BYTES = 10 * 1024 * 1024;

const STEP_LABELS = ["Details", "Research"] as const;

const SIDEBAR_FEATURES = [
  "Attach an employer to every job you recruit for",
  "Arbi researches their mission, funding, and benefits",
  "Powers personalized outreach and candidate pitches",
];

export function CreateEmployerModal({
  open,
  onClose,
  onCreated,
}: CreateEmployerModalProps) {
  const [step, setStep] = useState<WizardStep>("details");
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setStep("details");
    setName("");
    setWebsite("");
    setCoverFile(null);
    setCoverPreview(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  useEffect(() => {
    if (!coverFile) {
      setCoverPreview(null);
      return;
    }
    const url = URL.createObjectURL(coverFile);
    setCoverPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [coverFile]);

  const uploadCover = useUploadEmployerCover();

  const create = useCreateEmployer({
    mutation: {
      onSuccess: (employer) => {
        onCreated?.(employer);
        handleClose();
      },
      onError: (err) => {
        const message =
          err instanceof Error ? err.message : "Could not create employer";
        showToast.error("Could not create employer", { description: message });
        setStep("details");
      },
    },
  });

  const enrichMutation = useMutation({
    mutationFn: (domain: string) =>
      customInstance<EnrichResult>("/api/employers/enrich", {
        method: "POST",
        body: JSON.stringify({ website: domain }),
      }),
  });

  const handleCoverPick = () => fileInputRef.current?.click();

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_COVER_BYTES) {
      showToast.error("Logo too large", { description: "Maximum size: 10MB." });
      return;
    }
    setCoverFile(file);
  };

  const handleCoverRemove = () => setCoverFile(null);

  const doCreate = useCallback(
    async (enrichment: EnrichResult | null) => {
      let cover_url: string | null = null;

      if (coverFile) {
        try {
          const result = await uploadCover.mutateAsync({
            data: { file: coverFile },
          });
          cover_url = result.url;
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Could not upload logo";
          showToast.error("Could not create employer", {
            description: message,
          });
          setStep("details");
          return;
        }
      }

      if (!cover_url && enrichment?.cover_url) {
        cover_url = enrichment.cover_url;
      }

      create.mutate({
        data: {
          name: name.trim(),
          website: website.trim() || undefined,
          cover_url,
          mission_vision: enrichment?.mission_vision ?? undefined,
          funding_traction: enrichment?.funding_traction ?? undefined,
          culture_principles: enrichment?.culture_principles ?? undefined,
          benefits: enrichment?.benefits ?? undefined,
        },
      });
    },
    [name, website, coverFile, create, uploadCover],
  );

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) return;

    if (!website.trim()) {
      await doCreate(null);
      return;
    }

    setStep("enriching");
    try {
      const result = await enrichMutation.mutateAsync(website.trim());
      await doCreate(result);
    } catch {
      await doCreate(null);
    }
  }, [name, website, enrichMutation, doCreate]);

  const isSubmitting =
    create.isPending || uploadCover.isPending || enrichMutation.isPending;
  const canSubmit = name.trim().length > 0 && !isSubmitting;

  // Uploaded logo wins; otherwise pull the logo from the website domain.
  // The first initial is used only when there's no logo AND no website.
  const fetchedLogo = logo({
    domain: websiteToDomain(website),
    nameFallback: false,
    size: 128,
    format: "png",
  });
  const logoSrc = coverPreview ?? fetchedLogo;

  const stepIndex = step === "details" ? 1 : 2;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <ModalContent size="lg" className="max-h-[92dvh] p-0 sm:max-w-180">
        <div className="sr-only">
          <ModalTitle>New employer</ModalTitle>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          className="hidden"
          onChange={handleCoverChange}
        />

        <div className="relative flex min-h-0 flex-col overflow-hidden sm:h-120 sm:flex-row">
          {/* ── Left branded sidebar ── */}
          <div className="hidden w-[280px] shrink-0 flex-col justify-between border-r border-border bg-white px-7 py-8 sm:flex">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <Icon name="IconBag" size={22} className="text-black" />
                <span className="text-base font-semibold text-black">
                  Employers
                </span>
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <h2 className="text-2xl font-semibold leading-tight text-black">
                  Create Employer
                </h2>
                <p className="text-[13px] leading-relaxed text-black/60">
                  Add their website and Arbi researches the company to build
                  the profile for you.
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-col gap-3">
              {SIDEBAR_FEATURES.map((f) => (
                <div key={f} className="flex items-center gap-2.5">
                  <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-black/10">
                    <Icon
                      name="IconCheckmark1"
                      size={12}
                      className="text-black"
                    />
                  </div>
                  <span className="text-[13px] text-black/80">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right content panel ── */}
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-surface-elevated">
            <button
              type="button"
              onClick={handleClose}
              className="absolute right-3.5 top-3.5 z-10 flex size-6 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-contrast/6"
            >
              <Icon
                name="IconCrossMedium"
                size={14}
                className="text-contrast/40 hover:text-contrast/70"
              />
            </button>

            {/* Step indicators */}
            <div className="flex items-center gap-3 px-5 pb-2 pr-10 pt-5 sm:px-6 sm:pr-12 sm:pt-6">
              {STEP_LABELS.map((label, i) => (
                <div key={label} className="flex min-w-0 items-center gap-3">
                  {i > 0 && <div className="h-px w-8 shrink-0 bg-contrast/10" />}
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium",
                        stepIndex >= i + 1
                          ? "bg-white text-black"
                          : "bg-contrast/6 text-contrast/30",
                      )}
                    >
                      {i + 1}
                    </div>
                    <span
                      className={cn(
                        "truncate text-[13px] font-medium",
                        stepIndex >= i + 1 ? "text-heading" : "text-contrast/30",
                      )}
                    >
                      {label}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {step === "details" && (
                <motion.div
                  key="details"
                  className="flex min-h-0 min-w-0 flex-1 flex-col px-5 py-4 sm:px-6"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.15 }}
                >
                  <DetailsStep
                    name={name}
                    onNameChange={setName}
                    website={website}
                    onWebsiteChange={setWebsite}
                    logoSrc={logoSrc}
                    hasUpload={!!coverFile}
                    onCoverPick={handleCoverPick}
                    onCoverRemove={handleCoverRemove}
                    onCancel={handleClose}
                    onSubmit={handleSubmit}
                    canSubmit={canSubmit}
                    submitting={isSubmitting}
                    submitLabel="Create"
                  />
                </motion.div>
              )}

              {step === "enriching" && (
                <motion.div
                  key="enriching"
                  className="flex min-h-0 min-w-0 flex-1 flex-col px-5 py-4 sm:px-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.4 }}
                >
                  <EmployerResearchScreen name={name} logoSrc={logoSrc} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}

function DetailsStep({
  name,
  onNameChange,
  website,
  onWebsiteChange,
  logoSrc,
  hasUpload,
  onCoverPick,
  onCoverRemove,
  onCancel,
  onSubmit,
  canSubmit,
  submitting,
  submitLabel,
}: {
  name: string;
  onNameChange: (value: string) => void;
  website: string;
  onWebsiteChange: (value: string) => void;
  logoSrc: string | null;
  hasUpload: boolean;
  onCoverPick: () => void;
  onCoverRemove: () => void;
  onCancel: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
  submitting: boolean;
  submitLabel: string;
}) {
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const id = window.setTimeout(() => nameInputRef.current?.focus(), 100);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        <div className="flex items-start gap-5">
          <Avatar className="size-[72px] shrink-0 rounded-lg border border-border bg-contrast/4">
            <AvatarImage src={logoSrc} alt="" className="rounded-lg" />
            <AvatarFallback className="rounded-lg bg-transparent text-contrast/30">
              {name.trim() ? (
                <span className="text-2xl font-semibold">
                  {name.trim()[0].toUpperCase()}
                </span>
              ) : (
                <Icon name="IconCamera1" size={20} />
              )}
            </AvatarFallback>
          </Avatar>

          <div className="flex flex-1 min-w-0 flex-col justify-between self-stretch">
            <div className="flex flex-col gap-0.5">
              <Label className="text-heading">Logo</Label>
              <p className="text-xs text-contrast/60">
                PNG or JPEG under 10MB. No logo? We&apos;ll pull it from the
                website.
              </p>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="h-7 px-2 py-1 gap-1 text-sm"
                onClick={onCoverPick}
                icon={<Icon name="IconCamera1" size={16} />}
              >
                Upload logo
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-7 px-2 py-1 gap-1 text-sm"
                onClick={onCoverRemove}
                disabled={!hasUpload}
                icon={<Icon name="IconTrashCanSimple" size={14} />}
              >
                Remove logo
              </Button>
            </div>
          </div>
        </div>

        <div className="h-px shrink-0 bg-border" />

        <div className="flex flex-col gap-1.5">
          <Label className="text-heading">
            Employer name
            <span className="ml-0.5 text-red-300">*</span>
          </Label>
          <Input
            ref={nameInputRef}
            variant="modal"
            size="default"
            placeholder="e.g. Acme Inc."
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) {
                e.preventDefault();
                onSubmit();
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex flex-col gap-0.5">
            <Label className="text-heading">
              Website{" "}
              <span className="font-normal text-contrast/40">(optional)</span>
            </Label>
            <p className="text-xs text-contrast/60">
              Recommended. We&apos;ll research the company and auto-fill their
              profile and logo.
            </p>
          </div>
          <Input
            variant="modal"
            size="default"
            placeholder="e.g. acme.com"
            value={website}
            onChange={(e) => onWebsiteChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) {
                e.preventDefault();
                onSubmit();
              }
            }}
          />
        </div>
      </div>

      <div className="mt-3 flex shrink-0 items-center justify-end gap-3 border-t border-border pt-3">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <SubmitButton
          type="button"
          variant="primary"
          size="sm"
          onClick={onSubmit}
          disabled={!canSubmit}
          loading={submitting}
        >
          {submitLabel}
        </SubmitButton>
      </div>
    </>
  );
}
